import { useState } from 'react';
import { Claim, ClaimStatus } from '@/lib/types';
import { cn } from '@/lib/utils';
import { 
  FileText, 
  Brain, 
  Shield, 
  Wrench, 
  Send, 
  Clock, 
  CheckCircle2, 
  XCircle,
  ChevronRight,
  Pause,
  Play
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ClaimDetailDrawer } from './ClaimDetailDrawer';

const STATUS_CONFIG: Record<ClaimStatus, { label: string; icon: typeof FileText; color: string }> = {
  note_signed: { label: 'Note Signed', icon: FileText, color: 'text-blue-400' },
  coding: { label: 'Auto Coding', icon: Brain, color: 'text-purple-400' },
  denial_check: { label: 'Denial Check', icon: Shield, color: 'text-amber-400' },
  auto_fix: { label: 'Auto-Fix', icon: Wrench, color: 'text-orange-400' },
  submitted: { label: 'Submitted', icon: Send, color: 'text-cyan-400' },
  pending: { label: 'Pending', icon: Clock, color: 'text-yellow-400' },
  paid: { label: 'Paid', icon: CheckCircle2, color: 'text-success' },
  denied: { label: 'Denied', icon: XCircle, color: 'text-destructive' },
  appealed: { label: 'Appealed', icon: FileText, color: 'text-orange-400' },
};

interface LiveClaimsPulseProps {
  claims: Claim[];
  isPaused: boolean;
  onTogglePause: () => void;
}

export function LiveClaimsPulse({ claims, isPaused, onTogglePause }: LiveClaimsPulseProps) {
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleClaimClick = (claim: Claim) => {
    setSelectedClaim(claim);
    setDrawerOpen(true);
  };

  return (
    <>
      <div className="glass-card-elevated flex flex-col h-[520px]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="pulse-dot bg-success" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Live Claims Pulse</h3>
              <p className="text-xs text-muted-foreground">Real-time processing</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onTogglePause}
            className="gap-2"
          >
            {isPaused ? (
              <>
                <Play className="w-3 h-3" />
                Resume
              </>
            ) : (
              <>
                <Pause className="w-3 h-3" />
                Pause
              </>
            )}
          </Button>
        </div>

        {/* Claims Feed */}
        <div className="flex-1 overflow-auto p-3 space-y-2 scrollbar-thin">
          {claims.slice(0, 20).map((claim, index) => {
            const statusConfig = STATUS_CONFIG[claim.status];
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={claim.id}
                onClick={() => handleClaimClick(claim)}
                className={cn(
                  "live-feed-item",
                  index === 0 && !isPaused && "border-primary/30"
                )}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between gap-3">
                  {/* Left: Status & Patient */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn("p-1.5 rounded-md bg-secondary", statusConfig.color)}>
                      <StatusIcon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">
                          {claim.patientInitials}
                        </span>
                        <span className="text-xs font-medium truncate">
                          {claim.encounterType}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground">
                          {claim.payer}
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {claim.cptCodes[0]}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Center: Amount & Risk */}
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-mono text-sm font-medium">
                        ${claim.expectedAmount.toLocaleString()}
                      </div>
                      <div className={cn(
                        "text-[10px] font-medium",
                        claim.denialRisk < 15 && "risk-low",
                        claim.denialRisk >= 15 && claim.denialRisk < 30 && "risk-medium",
                        claim.denialRisk >= 30 && "risk-high"
                      )}>
                        {claim.denialRisk}% risk
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className={cn(
                      "status-badge",
                      claim.status === 'paid' && "status-badge-success",
                      claim.status === 'denied' && "status-badge-danger",
                      claim.status === 'pending' && "status-badge-warning",
                      !['paid', 'denied', 'pending'].includes(claim.status) && "status-badge-info"
                    )}>
                      {statusConfig.label}
                    </div>

                    {/* Agent Actions Count */}
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <span className="text-[10px]">{claim.agentActions.length} actions</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Stats */}
        <div className="p-3 border-t border-border bg-secondary/30">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-success" />
                <span className="text-muted-foreground">Processing</span>
                <span className="font-mono font-medium">{claims.filter(c => !['paid', 'denied'].includes(c.status)).length}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Completed</span>
                <span className="font-mono font-medium">{claims.filter(c => c.status === 'paid').length}</span>
              </div>
            </div>
            <span className="text-muted-foreground">Last 50 claims</span>
          </div>
        </div>
      </div>

      <ClaimDetailDrawer 
        claim={selectedClaim} 
        open={drawerOpen} 
        onOpenChange={setDrawerOpen}
      />
    </>
  );
}
